import { ChartContainer } from '@components/chart';
import ErrorBoundary from '@components/ErrorBoundary';
import { SankySVG } from '@components/icon/svg';
import CodeQuotesModal from '@components/interview/Analysis/CodeQuotesModal';

import CategoryUpdateModal from '@components/modal/CategoryUpdateModal';
import Spinner from '@components/spinner/Spinner';
import { useFetchInterviewCodebook, useFetchTranscriptList } from '@hooks/getQueries';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import formatString from '@utils/formatString';
import { handleSaveImage } from '@utils/saveChartImage';
import ReactEcharts from 'echarts-for-react';
import _ from 'lodash';
import { forwardRef, Suspense, useRef, useState } from 'react';
import { useParams } from 'react-router';

function generateThemeColor(index, total) {
  const hue = (540 + index * (360 / total)) % 360;
  return `hsl(${hue}, 140%,30%)`;
}
const SankeyChart = forwardRef(
  (
    {
      themes = [],
      categories = {},
      codesQuotesDict = {},
      isShowInterpretation = true,
      setFetchThemeData,
      fetchThemesAndCategories,
      setOpenThemeModal,
      selectedNode,
      setSelectedNode,
      getColorByCategory,
      canEdit,
    },
    ref,
  ) => {
    const [openCodeModal, setOpenCodeModal] = useState(false);
    const [openCategoryModal, setOpenCategoryModal] = useState(false);
    const [selectedCode, setSelectedCode] = useState(null);

    const { id } = useParams();
    const { data: transcripts } = useFetchTranscriptList(id);
    const { data: interviewCodebook, refetch } = useFetchInterviewCodebook({ studyId: id });
    const chartRef = useRef();

    const closeModal = () => {
      setOpenCodeModal(false);

      setSelectedCode(null);
    };
    const closeCategoryModal = () => {
      setOpenCategoryModal(false);
      setOpenThemeModal(false);
      setSelectedNode(null);
    };

    const generateSankeyData = () => {
      const nodes = [];
      const links = [];
      const nodeSet = new Set();
      _.forEach(themes, (theme, themeIndex) => {
        const baseHue = (themeIndex * 300) % 360;
        const themeColor = generateThemeColor(themeIndex, themes.length, baseHue);

        theme.color = themeColor;
        theme.categories_included_list = _.map(theme.categories_included, (category) => ({
          code_name: category,
          color: getColorByCategory(category),
        }));
      });

      if (!themes || themes.length === 0) {
        return { nodes, links };
      }

      // Grounded emergence permits a category to belong to more than one
      // theme, and a code to belong to more than one category. Node IDs
      // therefore have to be derived from the entity's own identity (the
      // raw name from the model), NOT the loop index — index-based IDs
      // let the same real category slip past nodeSet whenever it lands
      // at different positions across themes, drawing a duplicate node
      // stacked underneath the original. Level prefixes ("theme::" /
      // "category::" / "code::") keep the three columns from colliding
      // when an entity happens to share a display name across levels.
      themes.forEach((theme, themeIdx) => {
        // Themes are still index-suffixed so two themes that happen to
        // share a name stay distinct rows — themes ARE per-item entities
        // in this view, unlike categories/codes which merge on identity.
        const themeNode = `theme::${themeIdx}::${theme.name}`;
        if (!nodeSet.has(themeNode)) {
          nodes.push({
            name: themeNode,
            displayName: theme?.name,
            saliency: theme.saliency,
            color: theme.color.replace('hsl', 'hsla').replace(')', ', 0.3)'),
            firstNode: true,
            description: theme.description,
          });
          nodeSet.add(themeNode);
        }

        if (theme.categories_included_list && theme.categories_included_list.length > 0) {
          theme.categories_included_list.forEach((category) => {
            const categoryData = categories[category.code_name];
            if (categoryData) {
              const categoryNode = `category::${category.code_name}`;
              if (!nodeSet.has(categoryNode)) {
                nodes.push({
                  name: categoryNode,
                  displayName: `${formatString(category.code_name)}`,
                  saliency: categoryData.saliency,
                  color: getColorByCategory(category.code_name),
                  middleNode: true,
                  description: categoryData.description,
                });
                nodeSet.add(categoryNode);
              }

              // Link theme to category
              links.push({
                source: themeNode,
                target: categoryNode,
                value: categoryData.saliency,
              });
              // Add codes and link categories to codes

              if (categoryData.codes_included && categoryData.codes_included.length > 0) {
                categoryData.codes_included.forEach((code) => {
                  const codeNode = `code::${code}`;
                  let quote = codesQuotesDict && codesQuotesDict[code];

                  if (!nodeSet.has(codeNode)) {
                    nodes.push({
                      name: codeNode,
                      displayName: `${formatString(code || '')}`,
                      definition: quote?.definition,
                      location: 0,
                      saliency: quote?.sources?.length,
                      // A code can belong to multiple categories; keep
                      // the *first* category we see it under as the
                      // click-through anchor. The code-details modal
                      // uses categoryKey to hydrate context and doesn't
                      // change behaviour for the multi-parent case.
                      categoryKey: category.code_name,
                      dName: code,
                      color: category.color,
                      lastNode: true,
                      description: quote?.summary ?? '',
                    });

                    nodeSet.add(codeNode);
                  }

                  links.push({
                    source: categoryNode,
                    target: codeNode,
                    value: 1,
                  });
                });
              } else {
                console.warn(`Category "${category.code_name}" has no codes included.`);
              }
            } else {
              console.warn(`Category "${category.code_name}" not found in categories.`);
            }
          });
        } else {
          console.warn(`No categories included in theme "${theme.name}".`);
        }
      });

      return { nodes, links };
    };

    const { nodes, links } = generateSankeyData();

    const option = {
      series: [
        {
          type: 'sankey',
          layout: 'none',
          data: nodes.map((node) => ({
            ...node,
            value: 10,
            itemStyle: { color: node.color },
            // label: {
            //   formatter: (params) => params.displayName,
            //   fontSize: 13,
            // },
            emphasis: {
              focus: 'adjacency',
              label: {
                show: true,

                formatter: (params) => params.displayName,
              },
            },
          })),
          label: {
            show: true,
            fontSize: 13,
            color: '#000',
            formatter: (params) => {
              const node = nodes.find((node) => node.name === params.name);
              return node ? formatString(node?.displayName || '') : params.name;
            },
          },

          links: links,
          lineStyle: {
            color: 'source',
            curveness: 0.5,
          },
        },
      ],
      tooltip: {
        trigger: 'item',
        formatter: function (info) {
          if (info.dataType === 'node') {
            const { saliency, description } = info.data;
            return `
            <div style="padding: 8px; max-width: 400px; font-size: 12px;">
              <span style="display: block; white-space: normal; word-wrap: break-word;">${formatString(description.length >= 200 ? `${description.slice(0, 200)}...` : description)}</span><br/>
              <strong>Saliency : ${saliency}</strong> 
            </div>
          `;
          }
          return '';
        },
      },
    };

    const handleNodeClick = (params) => {
      if (params?.data?.lastNode) {
        setOpenCodeModal(true);
        setSelectedCode({
          categoryKey: params?.data?.categoryKey,
          definition: params?.data?.definition,
          location: 0,
          name: params?.data?.dName,
          saliency: params?.data?.saliency,
        });
      } else if (params?.data?.middleNode) {
        setOpenCategoryModal(true);
        setSelectedNode({
          name: params?.data?.displayName,
          saliency: params?.data?.saliency,
        });
      } else if (params?.data?.firstNode) {
        setOpenThemeModal(true);
        setSelectedNode({
          name: params?.data?.displayName,
          saliency: params?.data?.saliency,
        });
      }
    };
    const sankeyHeight = nodes.length * 10 < 100 ? null : nodes.length * 13;
    return (
      <Box ref={ref}>
        <ChartContainer
          hideToolbar={false}
          title={'Sankey Chart (Check full view for better visualization)'}
          fullHeight={800}
          height={sankeyHeight}
          icon={<SankySVG />}
          chartRef={chartRef}
          hideAddToReportButton
          handleSaveImage={() => handleSaveImage({ chartRef, fileName: 'Sankey Chart' })}
        >
          {isShowInterpretation && (
            <Typography variant="body2" mt={2}>
              <strong>How to interpret the Sankey Chart:</strong> The Sankey Chart shows the
              hierarchical flow from themes to categories and then to codes. Nodes represent
              elements in the hierarchy, and the path width indicates the relative saliency of each
              element. Hover over a node to view its saliency.
            </Typography>
          )}
          {nodes.length > 0 && links.length > 0 ? (
            <ReactEcharts
              ref={chartRef}
              option={option}
              onEvents={{ click: handleNodeClick }}
              style={{ minHeight: '100%', width: '100%' }}
            />
          ) : (
            <Typography variant="body2" color="error" mt={2}>
              No data available to display.
            </Typography>
          )}
        </ChartContainer>
        {openCodeModal && (
          <Dialog open={openCodeModal} onClose={closeModal} size="md" maxWidth="800px">
            <DialogTitle>
              <Typography variant="h6">Code Details</Typography>
            </DialogTitle>
            <IconButton
              onClick={closeModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            <Divider />
            <DialogContent
              sx={{ maxWidth: '90vw', minWidth: '600px', position: 'relative', padding: '0' }}
            >
              <ErrorBoundary>
                <Suspense fallback={<Spinner />}>
                  <CodeQuotesModal
                    transcripts={transcripts}
                    open={openCodeModal}
                    closeModal={closeModal}
                    studyId={id}
                    setFetchThemeData={setFetchThemeData}
                    openCode={selectedCode}
                    setSelectedCode={setSelectedCode}
                    canEdit={canEdit}
                  />
                </Suspense>
              </ErrorBoundary>
            </DialogContent>
          </Dialog>
        )}
        <CategoryUpdateModal
          openCategoryModal={openCategoryModal}
          closeCategoryModal={closeCategoryModal}
          interviewCodebook={interviewCodebook}
          setOpenCodeModal={setOpenCodeModal}
          setSelectedCode={setSelectedCode}
          fetchThemesAndCategories={fetchThemesAndCategories}
          selectedNode={selectedNode}
          id={id}
          refetch={refetch}
          getColorByCategory={getColorByCategory}
        />
      </Box>
    );
  },
);

SankeyChart.displayName = 'SankeyChart';

export default SankeyChart;
